import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Zap, TrendingUp, ArrowUpRight, Trash2, Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const fmt = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};

const fmtMs = (ms) => {
  if (ms == null) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
};

export const TokenUsageTab = ({ data, days, setDays }) => {
  if (!data) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
        No token usage data available yet. Token tracking starts from the next verification.
      </div>
    );
  }

  const { total, avg_per_test, by_product, deleted_tests, top_tests, latency, tokens_by_model } = data;

  return (
    <div className="space-y-4" data-testid="token-usage-tab">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          AI token consumption — {days === 0 ? "today" : `last ${days} days`}.{" "}
          <span className="text-xs">Only tests run after token tracking was enabled are counted.</span>
        </p>
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Today</SelectItem>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tokens</CardTitle>
            <Zap className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{fmt(total.total_tokens)}</div>
            <p className="text-xs text-muted-foreground">{total.test_count} tests tracked</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Prompt Tokens</CardTitle>
            <ArrowUpRight className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{fmt(total.prompt_tokens)}</div>
            <p className="text-xs text-muted-foreground">
              {total.total_tokens ? Math.round((total.prompt_tokens / total.total_tokens) * 100) : 0}% of total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Output Tokens</CardTitle>
            <ArrowUpRight className="h-4 w-4 text-emerald-500 rotate-90" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{fmt(total.output_tokens)}</div>
            <p className="text-xs text-muted-foreground">
              {total.total_tokens ? Math.round((total.output_tokens / total.total_tokens) * 100) : 0}% of total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg per Test</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{fmt(avg_per_test.total_tokens)}</div>
            <p className="text-xs text-muted-foreground">
              {fmt(avg_per_test.prompt_tokens)} in / {fmt(avg_per_test.output_tokens)} out
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* By product */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tokens by Product</CardTitle>
          </CardHeader>
          <CardContent>
            {by_product.length === 0 ? (
              <p className="text-sm text-muted-foreground">No data</p>
            ) : (
              <div className="space-y-3">
                {by_product.map((p) => (
                  <div key={p.product_name} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="truncate max-w-[180px]" title={p.product_name}>{p.product_name}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-muted-foreground">{p.test_count} tests</span>
                        <Badge variant="secondary">{fmt(p.total_tokens)}</Badge>
                      </div>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-500 rounded-full"
                        style={{ width: `${total.total_tokens ? (p.total_tokens / total.total_tokens) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top tests */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top Tests by Token Usage</CardTitle>
          </CardHeader>
          <CardContent>
            {top_tests.length === 0 ? (
              <p className="text-sm text-muted-foreground">No data</p>
            ) : (
              <div className="space-y-2">
                {top_tests.map((t, idx) => (
                  <div key={t.test_id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-mono text-muted-foreground w-4 shrink-0">{idx + 1}</span>
                      <div className="min-w-0">
                        <p className="truncate font-medium" title={t.product_name}>{t.product_name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {t.serial_number || "No S/N"} · {t.tester_name}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="shrink-0 ml-2 font-mono">
                      {fmt(t.total_tokens)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* AI Latency */}
      {latency?.by_model?.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {/* By model */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                AI Response Latency
              </CardTitle>
              <Badge variant="outline">{fmtMs(latency.avg_ms)} avg</Badge>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground border-b">
                    <th className="text-left pb-2 font-medium">Model</th>
                    <th className="text-center pb-2 font-medium">Steps</th>
                    <th className="text-center pb-2 font-medium">Avg</th>
                    <th className="text-center pb-2 font-medium">Min</th>
                    <th className="text-center pb-2 font-medium">Max</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {latency.by_model.map((m) => (
                    <tr key={m.model} className="hover:bg-muted/40">
                      <td className="py-2 pr-4 font-mono text-xs font-medium">{m.model}</td>
                      <td className="py-2 text-center text-muted-foreground">{m.count}</td>
                      <td className="py-2 text-center font-semibold">{fmtMs(m.avg_ms)}</td>
                      <td className="py-2 text-center text-emerald-600">{fmtMs(m.min_ms)}</td>
                      <td className="py-2 text-center text-red-500">{fmtMs(m.max_ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Slowest steps */}
          {latency.slowest_steps?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Slowest Steps</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-muted-foreground border-b">
                      <th className="text-left pb-2 font-medium">Step</th>
                      <th className="text-left pb-2 font-medium">Product</th>
                      <th className="text-center pb-2 font-medium">Status</th>
                      <th className="text-right pb-2 font-medium">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {latency.slowest_steps.map((s, i) => (
                      <tr key={i} className="hover:bg-muted/40">
                        <td className="py-2 pr-2 max-w-[120px] truncate font-medium" title={s.step_name}>{s.step_name}</td>
                        <td className="py-2 pr-2 max-w-[100px] truncate text-muted-foreground text-xs" title={s.product_name}>{s.product_name}</td>
                        <td className="py-2 text-center">
                          <Badge variant={s.status === "pass" ? "default" : s.status === "fail" ? "destructive" : "secondary"} className="text-xs">
                            {s.status}
                          </Badge>
                        </td>
                        <td className="py-2 text-right font-mono font-semibold text-red-500">{fmtMs(s.ai_elapsed_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Tokens by model */}
      {tokens_by_model?.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4 text-yellow-500" />
              Token Usage by Model
            </CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b">
                  <th className="text-left pb-2 font-medium">Model</th>
                  <th className="text-center pb-2 font-medium">Steps</th>
                  <th className="text-center pb-2 font-medium">Prompt</th>
                  <th className="text-center pb-2 font-medium">Output</th>
                  <th className="text-right pb-2 font-medium">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {tokens_by_model.map((m) => (
                  <tr key={m.model} className="hover:bg-muted/40">
                    <td className="py-2 pr-4 font-mono text-xs font-medium">{m.model}</td>
                    <td className="py-2 text-center text-muted-foreground">{m.step_count}</td>
                    <td className="py-2 text-center text-blue-600 font-mono">{fmt(m.prompt_tokens)}</td>
                    <td className="py-2 text-center text-emerald-600 font-mono">{fmt(m.output_tokens)}</td>
                    <td className="py-2 text-right font-semibold font-mono">{fmt(m.total_tokens)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Deleted tests warning */}
      {deleted_tests.total_tokens > 0 && (
        <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-900">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Trash2 className="h-4 w-4 text-orange-500 shrink-0" />
              <div className="text-sm">
                <span className="font-medium text-orange-700 dark:text-orange-400">
                  {fmt(deleted_tests.total_tokens)} tokens
                </span>
                <span className="text-muted-foreground">
                  {" "}consumed by {deleted_tests.count} deleted test{deleted_tests.count !== 1 ? "s" : ""} (still counted above)
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
